import {
  faclipboardcheckclassicregular,
  faboxespackingclassicregular,
  fatruckclassicregular,
  fatruckrampboxclassicregular,
  faboltclassicregular,
  fatruckclockclassicregular
} from '@grc/ui-package';
import I18N from '../../../i18n/interface';
import {
  quoteManagement,
  shipmentManagement,
  expediteShipments
} from '../../../common/constants/url-constants';
import { ShipmentTabKey } from '../../../models/shipments/shipment-tab.interface';
import { QuoteTabKey } from '../../../models/quotes/quote-tab.interface';
import {
  DashboardCardType,
  DashboardTabType,
  QuoteWorkItemCardType
} from '../../../models/home/work-item.enum';

// Identifies which MyWorkItemsSummary card/tab this section's count comes from, and which
// MyWorkItemsDetails card/tab the drill-down grid should request when the section is clicked.
// countsKey is the key into that card's Counts dictionary (e.g. 'Scheduled_Pickups').
// A discriminated union since the two domains hit different services (Order.Service vs
// Quotes.Service) with different summary shapes - the Quote variant has no Details/tabType
// equivalent (drill-down there just navigates to the Quote Management tab via `tabKey`).
export type IOperationalCardSectionWorkItem =
  | {
      source?: 'order';
      cardType: DashboardCardType;
      tabType: DashboardTabType;
      countsKey: string;
    }
  | {
      source: 'quote';
      cardType: QuoteWorkItemCardType;
      countsKey: string;
    };

export interface IOperationalCardSectionConfig {
  key: string;
  labelKey: keyof I18N;
  // Which Shipments/Quote Management tab this section should pre-select on
  // navigate (see docs/shipment-management-navigation-plan.md §4 and
  // docs/quote-management-navigation-plan.md §7). Undefined defaults to
  // 'all' (Shipments only - every Quote Management section sets this
  // explicitly since that page's default tab is 'quoteRequests', not 'all').
  tabKey?: ShipmentTabKey | QuoteTabKey;
  // Present for sections backed by a MyWorkItemsSummary card, whether Order.Service's (Shipments-
  // domain, `source: 'order'`/omitted) or Quotes.Service's (`source: 'quote'`). weeklySchedules
  // has no MyWorkItems equivalent and is left out, rendering 0.
  workItem?: IOperationalCardSectionWorkItem;
}

export interface IOperationalCardConfig {
  icon: string;
  titleKey: keyof I18N;
  routePath: string;
  // Breadcrumb text for this card's deep-link, since Home-card navigation
  // sets location.state.breadCrumbLinks explicitly rather than going through
  // side-nav's leaf-text lookup (plan §3/§7 - useSideMenu.ts's
  // getMenuItemNameWithPath only runs for side-nav clicks).
  breadcrumbLabelKey: keyof I18N;
  sections: IOperationalCardSectionConfig[];
}

/**
 * Central catalog of every known Operational View card. This is the one file to
 * touch when the backend adds a new card type - the hook (`useOperationalViewSummary`)
 * merges live counts onto whatever is defined here, and the grid renders however many
 * entries this map has, in insertion order. No card-count/position assumptions live
 * anywhere else.
 */
export const operationalViewCardsConfig: Record<string, IOperationalCardConfig> =
  {
    PendingApprovals: {
      icon: faclipboardcheckclassicregular,
      titleKey: 'AllPendingApprovals',
      routePath: quoteManagement,
      breadcrumbLabelKey: 'QuoteManagement',
      sections: [
        {
          key: 'quoteRequests',
          labelKey: 'QuoteRequests',
          // MyWorkItemsSummary's Pending_Approvals count is quotes with InternalApproval.Status ==
          // Sent (Quotes.Service's QuoteQueryExtensions), which is QuoteStatus.SubmittedForApproval -
          // the `pendingForApproval` tab, not `quoteRequests` (QuoteStatus.Created). Pointed at
          // `pendingForApproval` so the drill-down grid matches what the card counted.
          tabKey: 'pendingForApproval',
          workItem: {
            source: 'quote',
            cardType: 'Pending_Approvals',
            countsKey: 'Pending_Approvals'
          }
        },
        { key: 'weeklySchedules', labelKey: 'WeeklySchedules' }
      ]
    },
    PickupsToday: {
      icon: faboxespackingclassicregular,
      titleKey: 'ShipmentsPickupForToday',
      routePath: shipmentManagement,
      breadcrumbLabelKey: 'ShipmentManagement',
      sections: [
        {
          key: 'scheduledPickups',
          labelKey: 'ScheduledPickups',
          tabKey: 'pickup',
          workItem: {
            cardType: 'PickUps',
            tabType: 'Scheduled',
            countsKey: 'Scheduled_Pickups'
          }
        },
        {
          key: 'alreadyPickedUp',
          labelKey: 'AlreadyPickedUp',
          // Already-picked-up-but-not-yet-delivered shipments surface under the InTransit tab
          // (MyWorkItemsDetailsPredicateBuilder has no separate PickedUp bucket in Details).
          tabKey: 'inTransit',
          workItem: {
            cardType: 'PickUps',
            tabType: 'Completed',
            countsKey: 'Already_Picked_Up'
          }
        }
      ]
    },
    InTransit: {
      icon: fatruckclassicregular,
      titleKey: 'TotalShipmentsInTransit',
      routePath: shipmentManagement,
      breadcrumbLabelKey: 'ShipmentManagement',
      sections: [
        {
          key: 'total',
          labelKey: 'Total',
          tabKey: 'inTransit',
          workItem: {
            cardType: 'InTransit',
            tabType: 'Total',
            countsKey: 'Total_InTransit'
          }
        }
      ]
    },
    DeliveriesToday: {
      icon: fatruckrampboxclassicregular,
      titleKey: 'ShipmentsDeliveryForToday',
      routePath: shipmentManagement,
      breadcrumbLabelKey: 'ShipmentManagement',
      sections: [
        {
          key: 'scheduledDeliveries',
          labelKey: 'ScheduledDeliveries',
          // Not-yet-delivered shipments (even ones with a scheduled delivery date) surface under
          // the InTransit tab - Details' Delivered tab only ever shows actually-delivered rows.
          tabKey: 'inTransit',
          workItem: {
            cardType: 'Deliveries',
            tabType: 'Scheduled',
            countsKey: 'Scheduled_Deliveries'
          }
        },
        {
          key: 'alreadyDelivered',
          labelKey: 'AlreadyDelivered',
          tabKey: 'delivered',
          workItem: {
            cardType: 'Deliveries',
            tabType: 'Completed',
            countsKey: 'Already_Delivered'
          }
        }
      ]
    },
    Expedites: {
      icon: faboltclassicregular,
      titleKey: 'TotalExpediteShipments',
      // Its own standalone screen, not a Shipments tab - see MyWorkItemsDetailsPredicateBuilder's
      // Expedites case (no card/tab selector, scoped to non-standard-service shipments).
      routePath: expediteShipments,
      breadcrumbLabelKey: 'ExpediteShipments',
      sections: [
        {
          key: 'total',
          labelKey: 'Total',
          workItem: {
            cardType: 'Expedites',
            tabType: 'Total',
            countsKey: 'Total_Expedites'
          }
        }
      ]
    },
    LateShipments: {
      icon: fatruckclockclassicregular,
      titleKey: 'TotalLateShipments',
      routePath: shipmentManagement,
      breadcrumbLabelKey: 'ShipmentManagement',
      sections: [
        {
          key: 'total',
          labelKey: 'Total',
          // No Details support for Late yet (Order.Service: "intentionally unimplemented pending
          // an ETA design") - falls back to the `all` tab like any section with no tabKey.
          workItem: {
            cardType: 'Late',
            tabType: 'Total',
            countsKey: 'Total_Late'
          }
        }
      ]
    }
  };
